import React, { useEffect, useState } from 'react';

import { StripeConnectedAccountInfo } from '@flipdish/api-client-typescript';
import { Button } from '@flipdish/ui-library/components/atoms/Button';
import { Select } from '@flipdish/ui-library/components/molecules/Select';
import { useMutation } from '@tanstack/react-query';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import {
  closeNotifySaving,
  notifyError,
  NotifyProps,
  notifySaved,
  notifySaving,
} from '../../../../../../layouts/Notify/actions';
import { bankingServices } from '../../../../../../services/banking.service';

export type OuterProps = {
  appId: string;
  stripeAccount: StripeConnectedAccountInfo;
  getBankAccountDetails: () => void;
};

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
type Props = OuterProps & MappedState & MappedDispatch;

const payoutIntervals = ['Manual', 'Daily', 'Weekly', 'Monthly'];

const PayoutIntervalSelector = (props: Props) => {
  const {
    appId,
    closeNotifySaving,
    getBankAccountDetails,
    notifyError,
    notifySaved,
    notifySaving,
    stripeAccount,
    translate,
  } = props;
  const { StripeId, PayoutScheduleInterval } = stripeAccount;
  const [selectedInterval, setSelectedInterval] = useState<string>(
    PayoutScheduleInterval?.toString() ?? 'Manual'
  );

  useEffect(() => {
    setSelectedInterval(PayoutScheduleInterval?.toString() ?? 'Manual');
  }, [PayoutScheduleInterval]);

  const { mutateAsync: savePayoutSchedule, isPending } = useMutation({
    mutationFn: async () => {
      notifySaving();
      return await bankingServices.updatePayoutSchedule(appId, StripeId!, selectedInterval);
    },
    onSuccess: () => {
      getBankAccountDetails();
      closeNotifySaving();
      notifySaved();
    },
    onError: () => {
      closeNotifySaving();
      notifyError({ message: 'Error_please_try_again_later', translate: true });
    },
  });

  return (
    <div
      data-fd="payout-interval-selector"
      className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 w-full sm:w-auto"
    >
      <Select
        label={translate('Payout_Interval') as string}
        selectedKey={selectedInterval}
        onSelectionChange={(key) => setSelectedInterval(key as string)}
        size="md"
        className="w-full sm:min-w-[200px]"
      >
        {payoutIntervals.map((interval) => (
          <Select.Item key={interval} id={interval} label={interval} textValue={interval} />
        ))}
      </Select>
      <Button
        color="primary"
        size="md"
        isDisabled={isPending || selectedInterval === PayoutScheduleInterval?.toString()}
        onPress={() => savePayoutSchedule()}
        className="w-full sm:w-auto"
        data-fd="save-payout-interval"
      >
        <Translate id="Save" />
      </Button>
    </div>
  );
};

const mapStateToPropsFactory = (initialState: AppState) => {
  const { locale } = initialState;
  return (state: AppState) => ({
    translate: getTranslate(locale),
  });
};

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  closeNotifySaving: () => dispatch(closeNotifySaving()),
  notifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  notifySaving: () => dispatch(notifySaving({ persist: true })),
  notifySaved: () => dispatch(notifySaved()),
});

const EnhancedComponent = connect(
  mapStateToPropsFactory,
  mapDispatchToProps
)(PayoutIntervalSelector);
export { EnhancedComponent as PayoutIntervalSelector };
